import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { QuickSendEditorContext } from "contexts/QuickSendEditor";
import { QuickSendErrorContext } from "contexts/QuickSendError";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { PreviewMessage } from "schemas/dashboard";
import { HandleChangePreviewFunction } from "schemas/outreach";

import MergeTagWarning from "components/Editor/MergeTagWarning";
import Editor from "components/Editor/quicksend";
import AttachmentManager from "features/Influencer/ContactList/AttachmentManager";
import {
  HandleSendEmail,
  Template,
} from "features/Influencer/ContactList/schema";
import { makeDeepCopy } from "utils/updateLocalState";
import { usePageSize } from "utils/usePageSize";

import BottomToolbar from "../BottomToolbar";
import SelectTemplate from "./SelectTemplate";
import styles from "./styles";

interface PreviewIndividualProps {
  preview: PreviewMessage;
  changePreview: HandleChangePreviewFunction;
  index: number;
  handleSendEmail: HandleSendEmail;
  sendLoading: boolean;
  setUpdateOrgProfile?: Dispatch<SetStateAction<boolean>>;
}

export default function PreviewIndividual({
  preview,
  changePreview,
  index,
  handleSendEmail,
  sendLoading,
  setUpdateOrgProfile,
}: PreviewIndividualProps) {
  const { isOnboardingPage } = usePageSize();

  const { emailBrandDialogOpen } = useContext(OnboardingDialogsContext);

  // Contexts
  const { quickSendBrandId, referredEmail, previewLoading } = useContext(
    QuickSendDrawerContext,
  );
  const { errors } = useContext(QuickSendErrorContext);
  const { setTemplateForQuickSend, bentoBrand } = useContext(QuickSendContext);
  const { editor } = useContext(QuickSendEditorContext);

  const [attachmentDialogOpen, setAttachmentDialogOpen] =
    useState<boolean>(false);

  // Image properties
  const [selectImageOpen, setSelectImageOpen] = useState<boolean>(false);
  const redErrorHighlight = Number(errors?.outreachBody) === index;

  const removeAttachment = () => {
    changePreview("attachment", undefined, index);
    setTemplateForQuickSend((prev: Template[]) => {
      const copy = makeDeepCopy(prev);
      copy[index]["attachment"] = undefined;
      return copy;
    });
  };

  const attachment = preview?.attachment;
  const attachmentProps = {
    allowAttachment: true,
    openAttachmentManager: () => {
      setAttachmentDialogOpen(true);
    },
    attachment,
    removeAttachment,
    setSelectImageOpen,
  };

  const selectedImage = (imageUrl: string) => {
    if (editor) {
      editor.chain().focus().setImage({ src: imageUrl, size: "large" }).run();
    }
  };

  const editorLoading =
    previewLoading === quickSendBrandId || bentoBrand?.id !== quickSendBrandId;

  return (
    <>
      {redErrorHighlight && (
        <Typography variant="body2" sx={styles.errorText}>
          Please fill in the body of this email
        </Typography>
      )}
      <Box
        sx={[
          { position: "relative" },
          redErrorHighlight ? styles.errorContent : {},
        ]}
      >
        {/* Only show the select template once the brand matches the quick send as
        it is required for refetching previews */}
        {bentoBrand?.id === quickSendBrandId && (
          <SelectTemplate index={index} preview={preview} editor={editor} />
        )}
        <Box sx={styles.editorWrapper}>
          {editor && <Editor editor={editor} />}
          {editorLoading && (
            <Box
              sx={{
                p: 2,
                position: "absolute",
                bottom: 0,
                top: 0,
                background: "white",
                width: "100%",
              }}
            >
              <Skeleton variant="rounded" height={"100%"} width={"100%"} />
            </Box>
          )}

          {attachment && editor && (
            <Box sx={styles.attachmentContainer}>
              <Box className="fa-regular fa-paperclip" sx={{ mr: 1 }} />{" "}
              <Typography color="text.secondary" sx={styles.attachmentName}>
                {attachment.split("/")[attachment.split("/").length - 1]}
              </Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={attachmentProps.openAttachmentManager}
              >
                <Box component="i" className="fa-solid fa-edit" />
              </IconButton>
              {removeAttachment && (
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={removeAttachment}
                >
                  <Box component="i" className="fa-solid fa-trash" />
                </IconButton>
              )}
            </Box>
          )}
          {editor && (
            <MergeTagWarning
              editor={editor}
              subject={preview?.subject}
              missingValues={preview?.missingValues || []}
              hasMergeTagCondition={errors.mergeTag === index}
            />
          )}
        </Box>
      </Box>

      {/* Files Editor */}
      <AttachmentManager
        enteredSelection={(attachment: string) => {
          changePreview("attachment", attachment, index);
          setTemplateForQuickSend((prev: Template[]) => {
            const copy = makeDeepCopy(prev);
            copy[index]["attachment"] = attachment;
            return copy;
          });
        }}
        open={attachmentDialogOpen}
        setOpen={setAttachmentDialogOpen}
      />

      {/* Image Editor */}
      <AttachmentManager
        enteredSelection={selectedImage}
        open={selectImageOpen}
        setOpen={setSelectImageOpen}
        imageOnly={true}
      />

      {editor && (
        <BottomToolbar
          handleSendEmail={handleSendEmail}
          sendLoading={sendLoading}
          editor={editor}
          attachmentProps={attachmentProps}
          preview={preview}
          index={index}
          setUpdateOrgProfile={setUpdateOrgProfile}
          bentoBrand={bentoBrand}
          disabledSend={!bentoBrand?.id || bentoBrand?.id !== quickSendBrandId}
          hideCustomizeHelper={
            Boolean(referredEmail) || isOnboardingPage || emailBrandDialogOpen
          }
          showTestEmail
        />
      )}
    </>
  );
}
